/**
 * AssistantConfigPanel - Admin-only panel to configure AI Assistant API Key
 * Saves to user.attributes.form.assistantApiKey (hidden from regular users)
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { useTranslation } from '../common/components/LocalizationProvider';

const PROVIDERS = [
  {
    id: 'groq',
    name: 'Groq',
    url: 'https://console.groq.com/keys',
    free: true,
    models: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'mixtral-8x7b-32768'],
    defaultModel: 'llama-3.3-70b-versatile',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    url: 'https://platform.openai.com/api-keys',
    free: false,
    models: ['gpt-4o-mini', 'gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    defaultModel: 'gpt-4o-mini',
  },
  {
    id: 'claude',
    name: 'Claude (Anthropic)',
    url: 'https://console.anthropic.com/settings/keys',
    free: false,
    models: ['claude-sonnet-4-20250514', 'claude-3-5-haiku-20241022', 'claude-opus-4-20250514'],
    defaultModel: 'claude-sonnet-4-20250514',
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    url: 'https://aistudio.google.com/app/apikey',
    free: true,
    models: ['gemini-2.0-flash', 'gemini-2.0-flash-lite', 'gemini-1.5-pro'],
    defaultModel: 'gemini-2.0-flash',
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    url: 'https://platform.deepseek.com/',
    free: false,
    models: ['deepseek-chat', 'deepseek-coder'],
    defaultModel: 'deepseek-chat',
  },
  {
    id: 'ollama',
    name: 'Ollama (Local)',
    url: 'https://ollama.com/',
    free: true,
    models: ['llama3.2', 'phi3:mini', 'qwen2.5:1.5b', 'mistral'],
    defaultModel: 'llama3.2',
  },
];

const AssistantConfigPanel = () => {
  const t = useTranslation();
  const user = useSelector((state) => state.session.user);

  const [config, setConfig] = useState({
    assistantApiKey: '',
    assistantProvider: 'openai',
    assistantModel: '',
    assistantEnabled: true,
    assistantFallbackProviders: [], // Providers de fallback em ordem
    assistantProviderKeys: {}, // API keys por provider: { openai: 'sk-...', groq: 'gsk-...' }
  });
  const [showKey, setShowKey] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Load existing config from user attributes
  useEffect(() => {
    if (user?.attributes?.form) {
      const form = typeof user.attributes.form === 'string'
        ? JSON.parse(user.attributes.form)
        : user.attributes.form;

      setConfig({
        assistantApiKey: form.assistantApiKey || '',
        assistantProvider: form.assistantProvider || 'openai',
        assistantModel: form.assistantModel || '',
        assistantEnabled: form.assistantEnabled !== false,
        assistantFallbackProviders: form.assistantFallbackProviders || [],
        assistantProviderKeys: form.assistantProviderKeys || {},
      });
    }
  }, [user]);

  const toggleShowKey = (providerId) => {
    setShowKey((prev) => ({ ...prev, [providerId]: !prev[providerId] }));
  };

  const updateProviderKey = (providerId, key) => {
    setConfig((prev) => ({
      ...prev,
      assistantProviderKeys: {
        ...prev.assistantProviderKeys,
        [providerId]: key,
      },
    }));
  };

  const toggleFallback = (providerId) => {
    setConfig((prev) => {
      const current = prev.assistantFallbackProviders || [];
      if (current.includes(providerId)) {
        return { ...prev, assistantFallbackProviders: current.filter((p) => p !== providerId) };
      }
      return { ...prev, assistantFallbackProviders: [...current, providerId] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Get current user data
      const userResponse = await fetch(`/api/users/${user.id}`);
      if (!userResponse.ok) throw new Error('Erro ao buscar usuario');

      const userData = await userResponse.json();
      const currentAttributes = userData.attributes || {};
      const currentForm = typeof currentAttributes.form === 'string'
        ? JSON.parse(currentAttributes.form)
        : (currentAttributes.form || {});

      // Update form with new config
      const updatedForm = {
        ...currentForm,
        ...config,
      };

      // Update user
      const updateResponse = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...userData,
          attributes: {
            ...currentAttributes,
            form: updatedForm,
          },
        }),
      });

      if (!updateResponse.ok) throw new Error('Erro ao salvar');

      setMessage({ type: 'success', text: 'Configuracao salva com sucesso!' });

    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      // First save the config, then test
      await handleSave();

      const response = await fetch('/api/assistant/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages: [{ role: 'user', content: 'Diga apenas: OK' }],
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTestResult({ success: true, message: `Funcionando! Provedor: ${data.provider}, Modelo: ${data.model}` });
      } else {
        const error = await response.json();
        setTestResult({ success: false, message: error.error || 'Erro desconhecido' });
      }
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTesting(false);
    }
  };

  const selectedProvider = PROVIDERS.find((p) => p.id === config.assistantProvider);

  // Only show for admin users
  if (!user?.administrator) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h6">
          Configuracao do Assistente IA
        </Typography>
        <Chip label="Admin" size="small" color="warning" />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure a API Key para habilitar o assistente IA para todos os usuarios.
        A chave fica armazenada de forma segura e nao e visivel para usuarios comuns.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Enable/Disable */}
        <FormControlLabel
          control={
            <Switch
              checked={config.assistantEnabled}
              onChange={(e) => setConfig({ ...config, assistantEnabled: e.target.checked })}
            />
          }
          label="Habilitar Assistente IA"
        />

        <Divider />

        {/* Provider Selection */}
        <FormControl fullWidth>
          <InputLabel>Provedor Principal</InputLabel>
          <Select
            value={config.assistantProvider}
            label="Provedor Principal"
            onChange={(e) => setConfig({ ...config, assistantProvider: e.target.value, assistantModel: '' })}
          >
            {PROVIDERS.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {provider.name}
                  {provider.free && <Chip label="GRATIS" size="small" color="success" />}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Model Selection */}
        {selectedProvider && (
          <FormControl fullWidth>
            <InputLabel>Modelo</InputLabel>
            <Select
              value={config.assistantModel || selectedProvider.defaultModel}
              label="Modelo"
              onChange={(e) => setConfig({ ...config, assistantModel: e.target.value })}
            >
              {selectedProvider.models.map((model) => (
                <MenuItem key={model} value={model}>
                  {model}
                  {model === selectedProvider.defaultModel && (
                    <Chip label="padrao" size="small" sx={{ ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Help link */}
        {selectedProvider && (
          <Alert severity="info" icon={false}>
            <Typography variant="body2">
              Obtenha sua API Key em:{' '}
              <Link href={selectedProvider.url} target="_blank" rel="noopener">
                {selectedProvider.url} <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
              </Link>
            </Typography>
          </Alert>
        )}

        {/* API Key for primary provider */}
        <TextField
          label={`API Key - ${selectedProvider?.name || 'Provider'}`}
          type={showKey[config.assistantProvider] ? 'text' : 'password'}
          value={config.assistantApiKey}
          onChange={(e) => setConfig({ ...config, assistantApiKey: e.target.value })}
          fullWidth
          placeholder={
            config.assistantProvider === 'groq' ? 'gsk_...' :
            config.assistantProvider === 'claude' ? 'sk-ant-...' :
            config.assistantProvider === 'gemini' ? 'AIza...' : 'sk-...'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => toggleShowKey(config.assistantProvider)} edge="end">
                  {showKey[config.assistantProvider] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Divider />

        {/* Fallback Providers */}
        <Typography variant="subtitle2" color="text.secondary">
          Providers de Fallback (se o principal falhar)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PROVIDERS.filter((p) => p.id !== config.assistantProvider).map((provider) => {
            const isSelected = (config.assistantFallbackProviders || []).includes(provider.id);
            const hasKey = config.assistantProviderKeys?.[provider.id];
            return (
              <Chip
                key={provider.id}
                label={provider.name}
                onClick={() => toggleFallback(provider.id)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                icon={hasKey ? <CheckCircleIcon /> : undefined}
                sx={{ cursor: 'pointer' }}
              />
            );
          })}
        </Box>

        {/* API Keys for fallback providers */}
        {(config.assistantFallbackProviders || []).length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Configure as API Keys dos providers de fallback:
            </Typography>
            {(config.assistantFallbackProviders || []).map((providerId) => {
              const provider = PROVIDERS.find((p) => p.id === providerId);
              if (!provider) return null;
              return (
                <TextField
                  key={providerId}
                  label={`API Key - ${provider.name}`}
                  type={showKey[providerId] ? 'text' : 'password'}
                  value={config.assistantProviderKeys?.[providerId] || ''}
                  onChange={(e) => updateProviderKey(providerId, e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={provider.url}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => toggleShowKey(providerId)} edge="end" size="small">
                          {showKey[providerId] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              );
            })}
          </Box>
        )}

        <Divider />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !config.assistantApiKey}
          >
            {saving ? 'Salvando...' : 'Salvar Configuracao'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleTest}
            disabled={testing || !config.assistantApiKey}
          >
            {testing ? 'Testando...' : 'Testar Conexao'}
          </Button>
        </Box>

        {/* Test Result */}
        {testResult && (
          <Alert
            severity={testResult.success ? 'success' : 'error'}
            icon={testResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
          >
            {testResult.message}
          </Alert>
        )}
      </Box>
    </Paper>
  );
};

export default AssistantConfigPanel;
